/* eslint-disable new-cap */
import type { HighlighterGeneric } from "shiki"

import { shikiToMonaco, textmateThemeToMonacoTheme } from "@shikijs/monaco"
import * as monaco from "monaco-editor"
import editorWorker from "monaco-editor/esm/vs/editor/editor.worker?worker"
import cssWorker from "monaco-editor/esm/vs/language/css/css.worker?worker"
import htmlWorker from "monaco-editor/esm/vs/language/html/html.worker?worker"
import jsonWorker from "monaco-editor/esm/vs/language/json/json.worker?worker"
import tsWorker from "monaco-editor/esm/vs/language/typescript/ts.worker?worker"
import { createHighlighter } from "shiki"

import { languages } from "~/utils/languages"

const langs = ["applescript", "astro", "bash", "c", "c#", "c++", "clj", "clojure", "cmake", "cmd", "coffee", "coffeescript", "console", "cpp", "cs", "csharp", "css", "csv", "d", "dart", "dax", "diff", "docker", "dockerfile", "dotenv", "elixir", "elm", "erl", "erlang", "f", "f#", "fish", "fs", "fsharp", "go", "gql", "graphql", "groovy", "handlebars", "haskell", "hlsl", "html", "java", "javascript", "jinja", "jl", "js", "json", "jsonl", "jsx", "julia", "kotlin", "latex", "lisp", "log", "lua", "make", "makefile", "markdown", "md", "mermaid", "nginx", "nim", "nix", "objc", "objective-c", "ocaml", "perl", "php", "powershell", "prolog", "proto", "protobuf", "ps", "ps1", "py", "python", "r", "rb", "reg", "regex", "regexp", "rs", "rst", "ruby", "rust", "sass", "scala", "scheme", "scss", "sh", "shader", "shell", "smalltalk", "solidity", "sparql", "spl", "sql", "svelte", "swift", "tcl", "terraform", "tf", "toml", "ts", "tsx", "typescript", "vimscript", "vue", "xml", "yaml", "yml", "zig", "zsh"]

/**
 * NOTE: This is a single, global monaco editor state object, which stores a single
 * instance of the shiki highlighter and contains other monaco specific functions. If
 * you want a monaco renderer, you have to call `createMonacoRenderer`
 */

// https://github.com/vitejs/vite/discussions/1791
// eslint-disable-next-line no-restricted-globals
self.MonacoEnvironment = {
  getWorker(_, label) {
    if (label === "json") {
      return new jsonWorker()
    }
    if (label === "css" || label === "scss" || label === "less") {
      return new cssWorker()
    }
    if (label === "html" || label === "handlebars" || label === "razor") {
      return new htmlWorker()
    }
    if (label === "typescript" || label === "javascript") {
      return new tsWorker()
    }
    return new editorWorker()
  },
}
if (monaco.languages)
  monaco.languages.typescript.typescriptDefaults.setEagerModelSync(true)

interface State {
  /** use `getSyntaxHighlighter` instead */
  highlighter?: HighlighterGeneric<any, any>
}

let state: Ref<State>

function defaultState(): State {
  return {}
}
function clearMonacoEditorState() {
  state.value = defaultState()
}
async function setupSyntaxHighlighter() {
  if (isDefined(state.value.highlighter))
    return state.value.highlighter
  state.value.highlighter = await createHighlighter({
    themes: ["github-dark"],
    langs, // use vscode's syntax highlighter but shiki's themes
  })
  langs.forEach(id => monaco.languages.register({ id }))

  shikiToMonaco(state.value.highlighter, monaco)

  const theme = textmateThemeToMonacoTheme(state.value.highlighter.getTheme("github-dark")) as monaco.editor.IStandaloneThemeData
  theme.colors["editor.background"] = "#00000000"
  theme.colors.focusBorder = "#00000000"
  theme.rules.push({ background: "212123", token: "" })
  monaco.editor.defineTheme("github-dark", theme)

  // Register the themes from Shiki, and provide syntax highlighting for Monaco.
  return state.value.highlighter
}

async function createMonacoRenderer(element: HTMLElement, peek?: boolean, options: monaco.editor.IStandaloneDiffEditorConstructionOptions = {}) {
  await setupSyntaxHighlighter()
  return monaco.editor.create(element, {
    value: "Loading...",
    language: "javascript",
    theme: "github-dark",
    automaticLayout: true,
    fontFamily: "DM Mono",
    fontSize: 12,
    readOnly: true,
    roundedSelection: false,
    glyphMargin: true,
    padding: {
      top: peek ? 0 : 52, // $nav-h-default
    },
    ...options,
  })
}

export default function () {
  state = useState("monaco-editor-state", defaultState)

  return {
    monacoEditorState: state,
    clearMonacoEditorState,
    setupSyntaxHighlighter,
    createMonacoRenderer,
    languages,
  }
}
